import React, { useState } from 'react';

// --- Material-UI Core Components ---
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Tooltip,
  Card,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import CircleIcon from '@mui/icons-material/Circle'; // Import the icon

// --- Argon Design System Components ---
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// --- FAKE DATA FOR DEMONSTRATION ---
const FAKE_PRODUCT_DATA = [
  {
    org_id: '00Dd2000008w1glEAA',
    sf_id: '01td2000005uyt5AAA',
    external_id: '01td2000005uyt5AAA',
    name: 'CPU 1.6GHz i5',
    related_data: {
      productOptions: [],
      product: {
        sfId: '01td2000005uyt5AAA',
        name: 'CPU 1.6GHz i5',
        id: '01td2000005uyt5AAA',
        fields: null
      },
      optionConstraints: [],
      features: [],
      productRules: [],
      costbooks: [
        {
          sfId: '01sd2000003eSDZAA2',
          costbookEntries: [
            {
              sfId: '01ud2000001TjUhAAK',
              productIdentifier: 'CPU 1.6GHz i5',
              productId: '01td2000005uyt5AAA',
              costbookIdentifier: 'Standard Cost Book',
              id: '01ud2000001TjUhAAK',
              fields: {
                CurrencyIsoCode: 'EUR',
                UseStandardPrice: 'false',
                UnitPrice: '100.00',
                IsActive: 'true',
                ProductCode: 'CPU16GHZI5'
              }
            }
          ],
          name: 'Standard Cost Book',
          id: '01sd2000003eSDZAA2',
          fields: { IsActive: 'true', Name: 'Standard Cost Book' }
        }
      ]
    },
    product_version: '1.0.0',
    id: '01td2000005uyt5AAA',
    created_by: '6752cea3f1a3ff85c3b9a78d',
    created_date: '2025-05-19T11:47:15.584Z'
  },
];
// --- END OF FAKE DATA ---

function CostbooksTab({ product, renderFieldsProp }) {
  const [expandedCostbook, setExpandedCostbook] = useState({});

  if (!product || !FAKE_PRODUCT_DATA[0].related_data?.costbooks?.length) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Costbooks available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleCostbookClick = (costbookId) => {
    setExpandedCostbook((prevState) => ({
      ...prevState,
      [costbookId]: !prevState[costbookId],
    }));
  };

  return (
    <ArgonBox p={3}>
      {FAKE_PRODUCT_DATA[0].related_data.costbooks.map((costbook) => {
        const costbookId = costbook.sfId || costbook.id;
        const hasCostbookEntries = costbook.costbookEntries && costbook.costbookEntries.length > 0;

        return (
          <Card key={costbookId} sx={{ marginBottom: '1rem' }}>
            <ArgonBox mb={0.5} mt={0.5}>
              {/* --- Clickable Header for each Costbook --- */}
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleCostbookClick(costbookId)}
                style={{ cursor: 'pointer' }}
                p={1}
                borderRadius="md"
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                  {costbook.fields?.Name || costbook.Name || costbook.name}
                </ArgonTypography>
                <IconButton
                  aria-label="expand costbook"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent parent onClick from firing again
                    handleCostbookClick(costbookId);
                  }}
                >
                  <Icon>{expandedCostbook[costbookId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              {/* --- Collapsible Content for the Costbook --- */}
              <Collapse in={expandedCostbook[costbookId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2}>
                  {/* Render general costbook fields */}
                  {renderFieldsProp(costbook.fields)}

                  {/* --- Table for Costbook Entries --- */}
                  {hasCostbookEntries && (
                    <ArgonBox mt={3}>
                      <TableContainer component={Paper}>
                        <Table size="small" aria-label="costbook entries">
                          <TableHead sx={{ display: 'contents' }}>
                            <TableRow>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                                  Unit Price
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                                  Currency
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                                  Status
                                </ArgonTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {costbook.costbookEntries.map((entry) => (
                              <TableRow key={entry.sfId || entry.id}>
                                <TableCell>
                                  {entry.fields?.UnitPrice != null ? entry.fields.UnitPrice : 'N/A'}
                                </TableCell>
                                <TableCell>{entry.fields?.CurrencyIsoCode || 'EUR'}</TableCell>
                                <TableCell>
                                  <Tooltip title={entry.fields?.IsActive ? 'Active' : 'Inactive'}>
                                    {entry.fields?.IsActive ? (
                                      // Green icon for Active
                                      <CircleIcon color="success" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                                    ) : (
                                      // Grey icon for Inactive
                                      <CircleIcon color="disabled" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                                    )}
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </ArgonBox>
                  )}
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default CostbooksTab;
